/**
 * useSetPropertyAmenities — TanStack mutation over
 * `PUT /organizations/:slug/properties/:propertyId/amenities` (SCRUM-141 /
 * DS-010-5).
 *
 * This is a FULL-REPLACE assignment: the body `{ items: [{ amenityId, note? }] }`
 * becomes the property's complete amenity set (empty `items` clears all) and
 * the BE answers 204 No Content. The operation is idempotent, so the mutation
 * is OPTIMISTIC (plan §"Optimistic set" — ticket-mandated; the rest of the
 * property slice stays invalidate-only):
 *
 *   - onMutate:  cancel in-flight refetches, SNAPSHOT the current
 *                `propertyAmenities` cache, build an optimistic grouped
 *                response from `items` + the cached catalog (notes normalized
 *                to match the server-reconciled value), and write it.
 *   - onError:   ROLL BACK to the snapshot.
 *   - onSettled: invalidate `propertyAmenities` so the next read reconciles.
 *
 * Typed error kinds mirror `use-update-property-status.ts` (attach `kind` to
 * the thrown Error; derive `errorKind` from `mutation.error`): every documented
 * PUT failure maps to a distinct kind so the editor can surface distinct inline
 * `role="alert"` copy — 403 forbidden, 404 not-found, 409 conflict-closed
 * (org closed), 422 unknown-amenity (id inactive/removed since catalog fetch),
 * else other.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { components } from '@artemis/contracts';
import type { SetPropertyAmenitiesItem } from '@artemis/validators';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { propertyQueryKeys } from './queryKeys.js';
import { normalizeNote } from './amenity-note.js';
import { orderGroupsByCategory } from './amenities-mapper.js';

type AmenityCatalogListResponseDto =
  components['schemas']['AmenityCatalogListResponseDto'];
type PropertyAmenitiesListResponseDto =
  components['schemas']['PropertyAmenitiesListResponseDto'];
type PropertyAmenityGroupDto = components['schemas']['PropertyAmenityGroupDto'];
type PropertyAmenityItemDto = components['schemas']['PropertyAmenityItemDto'];

export type SetAmenitiesErrorKind =
  | 'forbidden'
  | 'not-found'
  | 'conflict-closed'
  | 'unknown-amenity'
  | 'other';

export interface UseSetPropertyAmenitiesResult {
  readonly mutateAsync: (
    items: readonly SetPropertyAmenitiesItem[],
  ) => Promise<void>;
  readonly status: 'idle' | 'pending' | 'success' | 'error';
  readonly errorKind: SetAmenitiesErrorKind | null;
  readonly reset: () => void;
}

/** Context carried from `onMutate` to `onError` for rollback. */
interface SetAmenitiesContext {
  /**
   * The pre-mutation cache value. `undefined` means the cache had no entry;
   * `onError` distinguishes that from a snapshotted value so it never writes
   * `undefined` back over a value another query populated.
   */
  readonly prev: PropertyAmenitiesListResponseDto | undefined;
}

type SetAmenitiesError = Error & { kind: SetAmenitiesErrorKind };

function setError(kind: SetAmenitiesErrorKind): SetAmenitiesError {
  const err = new Error(`set-property-amenities failed (${kind})`) as SetAmenitiesError;
  err.kind = kind;
  return err;
}

/**
 * Build the optimistic grouped response from the submitted `items` and the
 * cached catalog. Each item's `amenityId` is looked up in the catalog for its
 * `slug` / `label` / `category`; the note is normalized (same rule as the
 * PUT payload → optimistic == reconciled). Grouped by category and ordered via
 * `orderGroupsByCategory`. Items whose id is not in the catalog are dropped
 * from the optimistic view (they will 422 server-side; `onSettled` reconciles).
 */
function buildOptimisticGroups(
  items: readonly SetPropertyAmenitiesItem[],
  catalog: AmenityCatalogListResponseDto,
): PropertyAmenitiesListResponseDto {
  const byId = new Map(catalog.amenities.map((a) => [a.id, a]));
  const groupsByCategory = new Map<string, PropertyAmenityItemDto[]>();

  for (const item of items) {
    const catalogEntry = byId.get(item.amenityId);
    if (catalogEntry === undefined) {
      continue;
    }
    const optimisticItem: PropertyAmenityItemDto = {
      amenityId: item.amenityId,
      slug: catalogEntry.slug,
      label: catalogEntry.label,
      note: normalizeNote(item.note),
    };
    const bucket = groupsByCategory.get(catalogEntry.category);
    if (bucket === undefined) {
      groupsByCategory.set(catalogEntry.category, [optimisticItem]);
    } else {
      bucket.push(optimisticItem);
    }
  }

  const groups: PropertyAmenityGroupDto[] = [...groupsByCategory.entries()].map(
    ([category, amenities]) => ({
      category,
      amenities: [...amenities].sort((a, b) => a.label.localeCompare(b.label)),
    }),
  );

  return { groups: orderGroupsByCategory(groups) };
}

/** Read the raw catalog off the cache (or null if not cached). */
function readCachedCatalog(
  queryClient: QueryClient,
  orgSlug: string,
): AmenityCatalogListResponseDto | null {
  const raw = queryClient.getQueryData<AmenityCatalogListResponseDto>([
    ...propertyQueryKeys.amenitiesCatalog(),
    orgSlug,
  ]);
  return raw ?? null;
}

export function useSetPropertyAmenities(
  propertyId: string,
): UseSetPropertyAmenitiesResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  const amenitiesKey = [
    ...propertyQueryKeys.propertyAmenities(propertyId),
    orgSlug,
  ] as const;

  const mutation = useMutation<
    undefined,
    Error,
    readonly SetPropertyAmenitiesItem[],
    SetAmenitiesContext
  >({
    mutationFn: async (items) => {
      if (orgSlug === null) {
        throw new Error('Cannot set property amenities without an org slug');
      }
      const result = await apiClient.PUT(
        '/organizations/{slug}/properties/{propertyId}/amenities',
        {
          params: {
            path: { slug: orgSlug, propertyId },
          },
          body: { items: [...items] },
        },
      );
      if (result.response.status === 403) throw setError('forbidden');
      if (result.response.status === 404) throw setError('not-found');
      if (result.response.status === 409) throw setError('conflict-closed');
      if (result.response.status === 422) throw setError('unknown-amenity');
      if (!result.response.ok) throw setError('other');
      return undefined;
    },
    onMutate: async (items): Promise<SetAmenitiesContext> => {
      await queryClient.cancelQueries({ queryKey: amenitiesKey });
      const prev = queryClient.getQueryData<PropertyAmenitiesListResponseDto>(
        amenitiesKey,
      );

      // Optimistic write requires the catalog (for slug/label/category); if it
      // is not cached, skip the write and let `onSettled` invalidation
      // reconcile. The snapshot is still returned for a clean rollback.
      const catalog =
        orgSlug === null ? null : readCachedCatalog(queryClient, orgSlug);
      if (catalog !== null) {
        queryClient.setQueryData<PropertyAmenitiesListResponseDto>(
          amenitiesKey,
          buildOptimisticGroups(items, catalog),
        );
      }

      return { prev };
    },
    onError: (_error, _items, ctx) => {
      // Roll back only when a snapshot existed — never write `undefined` over
      // a value another in-flight query may have populated.
      if (ctx?.prev !== undefined) {
        queryClient.setQueryData(amenitiesKey, ctx.prev);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: amenitiesKey });
    },
  });

  const errorKind = ((): SetAmenitiesErrorKind | null => {
    if (mutation.status !== 'error' || mutation.error === null) return null;
    const e = mutation.error as Error & { kind?: SetAmenitiesErrorKind };
    return e.kind ?? 'other';
  })();

  return {
    mutateAsync: async (items) => {
      await mutation.mutateAsync(items);
    },
    status: mutation.status,
    errorKind,
    reset: mutation.reset,
  };
}
